/**
 * AI feature events — OCR, figures, voice capture, summaries, dictation and chat sessions.
 *
 * One slice of the `ANALYTICS_EVENTS` SSOT — see `../analytics.ts`, which
 * spreads every slice back into the single import surface call sites use.
 * The event NAME STRINGS are a PostHog dashboard contract: add and retire keys
 * here, never rename a value in place (see `rules/analytics.md`).
 */

export const AI_EVENTS = {
  // AI Features - OCR
  OCR_TRIGGERED: 'OCR Triggered',
  OCR_COMPLETED: 'OCR Completed',
  OCR_FAILED: 'OCR Failed',
  // Scientific capture figures. Properties are identifiers/counts/kinds and
  // degradation/zoom state only; never captions, OCR text, or image content.
  FIGURE_EXTRACTION_COMPLETED: 'Figure Extraction Completed',
  FIGURE_RECONSTRUCTION_OPENED: 'Figure Reconstruction Opened',
  FIGURE_RECONSTRUCTION_EDITED: 'Figure Reconstruction Edited',

  // AI Features - Voice
  VOICE_RECORDING_STARTED: 'Voice Recording Started',
  VOICE_RECORDING_COMPLETED: 'Voice Recording Completed',
  VOICE_RECORDING_FAILED: 'Voice Recording Failed',
  VOICE_RECORDING_UPLOAD_STARTED: 'Voice Recording Upload Started',
  VOICE_RECORDING_UPLOAD_COMPLETED: 'Voice Recording Upload Completed',
  VOICE_RECORDING_UPLOAD_FAILED: 'Voice Recording Upload Failed',
  VOICE_NOTE_DIARIZATION_ENABLED: 'Voice Note Diarization Enabled',
  // One per silence EPISODE, not per frame. The rate is the point: a silent mic
  // was indistinguishable from a working one until the transcript came back
  // empty. `source` splits the two capture surfaces.
  VOICE_RECORDING_SILENT_INPUT: 'Voice Recording Silent Input',
  // Durable note recordings (services/recording/durableRecording). BACKUP_FAILED:
  // the on-device copy could not be written (`stage` write) or cleared after
  // finalize (`stage` clear). The RECOVERY events follow a take found on this
  // device on load: OFFERED when the prompt shows it, RESTORED once its upload
  // finalized, RESTORE_FAILED when that upload failed, DISCARDED on a confirmed
  // discard. Properties: workspace_id, duration_ms, size_bytes,
  // was_complete (the take had stopped, vs the tab dying mid-take), age_ms.
  VOICE_RECORDING_BACKUP_FAILED: 'Voice Recording Backup Failed',
  VOICE_RECORDING_RECOVERY_OFFERED: 'Voice Recording Recovery Offered',
  VOICE_RECORDING_RECOVERY_RESTORED: 'Voice Recording Recovery Restored',
  VOICE_RECORDING_RECOVERY_RESTORE_FAILED: 'Voice Recording Recovery Restore Failed',
  VOICE_RECORDING_RECOVERY_DISCARDED: 'Voice Recording Recovery Discarded',
  // The "Not saved yet" prompt before an action that would interrupt work in
  // stores/unsavedWorkStore. `surface` is what asked (note_navigation,
  // workspace_switch), `choice` keep or proceed, `work` the labels at risk.
  // Replaced 'Recording Leave Prompt Answered' before that ever deployed.
  UNSAVED_WORK_PROMPT_ANSWERED: 'Unsaved Work Prompt Answered',

  // AI Features - Note Summary
  NOTE_SUMMARY_VIEWED: 'Note Summary Viewed',
  NOTE_SUMMARY_FEEDBACK_SUBMITTED: 'Note Summary Feedback Submitted',

  // AI Features - Voice Dictation (Bird Chat)
  VOICE_DICTATION_STARTED: 'Voice Dictation Started',
  VOICE_DICTATION_COMPLETED: 'Voice Dictation Completed',
  // error_type contract: backend ApiResponseError codes (SCREAMING_SNAKE,
  // e.g. TRANSCRIPTION_ERROR / AI_QUOTA_EXCEEDED) when the API supplied one;
  // legacy lowercase buckets otherwise — 'transcription_failed' (residual
  // backend-path failures), 'permission_denied', or Web Speech error enums
  // ('audio-capture', ...). Bucket dashboards on both conventions.
  VOICE_DICTATION_FAILED: 'Voice Dictation Failed',

  // AI Chat
  CHAT_SESSION_CREATED: 'Chat Session Created',
  CHAT_RESPONSE_RECEIVED: 'Chat Response Received',
  CHAT_RESPONSE_FAILED: 'Chat Response Failed',
  CHAT_SESSION_DELETED: 'Chat Session Deleted',
  CHAT_FILE_ATTACHED: 'Chat File Attached',
  CHAT_CANCELLED: 'Chat Cancelled',
  /**
   * The researcher asked for the transcript again after a failed history read.
   *
   * A LOWER BOUND on how often that read fails, not a count of failures — it
   * only fires when someone clicks. It is the signal we had none of: the failure
   * used to be entirely silent client-side, which is the bug the banner fixes.
   */
  CHAT_HISTORY_RETRIED: 'Chat History Retried',
  /**
   * The researcher opened another conversation (`destination: 'history'`) or
   * started a new one (`'new'`) while Bower was still answering. The turn
   * keeps running server-side; this counts how often people would rather not
   * wait for it.
   */
  CHAT_LEFT_STREAMING_TURN: 'Chat Left Streaming Turn',
  /**
   * The session-end rating (`session-end-rating-enabled`, agent quality plan
   * M3), one event per step, each discriminated by `surface` ('chat' under the
   * last reply, 'live' in the toast after End). Shown fires once per session
   * per page load; Submitted only once `POST /feedback` accepted the rating,
   * for the reason `CHAT_FEEDBACK_SUBMITTED` waits; Dismissed is the chat
   * row's close and the Live toast's close; Failed is a tap the route refused
   * (a Live trace can still be ingesting). Properties: surface, session_id,
   * trace_id; on Submitted and Failed also score (1 up, 0 down) and value
   * ('up' | 'down'). Submitted over Shown is the rating rate the plan's
   * 15% / 10% targets read.
   */
  SESSION_RATING_SHOWN: 'Session Rating Shown',
  SESSION_RATING_SUBMITTED: 'Session Rating Submitted',
  SESSION_RATING_DISMISSED: 'Session Rating Dismissed',
  SESSION_RATING_FAILED: 'Session Rating Failed',
} as const;
